import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import CurtainOpening from './CurtainOpening';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import { SkipForward } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GameIntroMontage = ({ player, onComplete, onSkip }) => {
  const [currentSegment, setCurrentSegment] = useState(0);
  const [showCurtain, setShowCurtain] = useState(true); // Start with curtain
  const [montageContent, setMontageContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [curtainPhase, setCurtainPhase] = useState('opening'); // 'opening', 'content', 'closing'
  const canvasRef = useRef(null);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.3) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Initialize M.ai.ry curated montage on mount
  useEffect(() => {
    createMairyCuratedMontage();
  }, []);

  const createMairyCuratedMontage = async () => {
    try {
      setIsLoading(true);
      
      // M.ai.ry creates artistic montage from uploaded content and consciousness themes
      const response = await axios.post(`${API}/mairy/create-cinematic-montage`, {
        player_id: player.id,
        montage_type: 'consciousness_expansion_intro',
        use_uploaded_content: true,
        artistic_vision: 'epic_consciousness_awakening',
        cinematic_style: 'mystical_documentary'
      });

      const curatedContent = response.data.montage_segments || [];
      
      // Enhanced with cinematic structure
      const cinematicMontage = curatedContent.map((segment, index) => ({
        ...segment,
        segment_number: index + 1,
        transition_type: 'curtain_fade',
        duration: segment.duration || 8000, // 8 seconds per segment
        visual_effects: segment.visual_effects || ['spiral_overlay', 'consciousness_glow']
      }));

      setMontageContent(cinematicMontage);
      
    } catch (error) {
      console.error('M.ai.ry montage creation error:', error);
      
      // Fallback to consciousness-themed segments
      setMontageContent([
        {
          segment_number: 1,
          type: 'consciousness_awakening',
          title: 'The Secret History Begins',
          content: 'Consciousness travelers gather at the threshold of remembering...',
          visual_effects: ['cosmic_spiral', 'golden_light'],
          duration: 6000
        },
        {
          segment_number: 2, 
          type: 'belonging_activation',
          title: 'The Great Remembering',
          content: 'When we belong to each other, fear dissolves. Solutions emerge and we create the reality we want to live in.',
          visual_effects: ['heartbeat_pulse', 'spiral_convergence'],
          duration: 7000
        },
        {
          segment_number: 3,
          type: 'reality_creation',
          title: 'Your Journey Begins',
          content: 'Reality created through play. Your consciousness expansion begins now.',
          visual_effects: ['portal_opening', 'timeline_activation'],
          duration: 5000
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Segment progression with curtain transitions
  const nextSegment = () => {
    if (currentSegment < montageContent.length - 1) {
      // Close curtain for transition
      setCurtainPhase('closing');
      setShowCurtain(true);
      
      setTimeout(() => {
        setCurrentSegment(prev => prev + 1);
        setCurtainPhase('opening');
        // Curtain opens for next segment
      }, 1500);
      
      setTimeout(() => {
        setShowCurtain(false);
        setCurtainPhase('content');
      }, 3500);
    } else {
      // Final transition to chat interface
      setCurtainPhase('closing');
      setShowCurtain(true);
      setTimeout(() => {
        onComplete();
      }, 2000);
    }
  };

  const skipMontage = () => {
    setCurtainPhase('closing');
    setShowCurtain(true);
    setTimeout(() => {
      onSkip();
    }, 1500);
  };

  // Auto-advance segments with cinematic timing
  useEffect(() => {
    if (!isLoading && montageContent.length > 0 && !showCurtain) {
      const segment = montageContent[currentSegment];
      const timer = setTimeout(() => {
        nextSegment();
      }, segment.duration || 6000);
      
      return () => clearTimeout(timer);
    }
  }, [currentSegment, showCurtain, isLoading, montageContent]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center space-y-4">
          <div 
            className="w-20 h-20 mx-auto"
            style={{ transform: `rotate(${spiralPhase}deg)` }}
          >
            <GrantitudeSpiral size={80} />
          </div>
          <p className="text-white text-xl" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
            M.ai.ry weaving consciousness montage...
          </p>
        </div>
      </div>
    );
  }

  const segment = montageContent[currentSegment] || {};

  return (
    <div className="min-h-screen relative overflow-hidden bg-black">
      
      {/* Always show curtain transitions */}
      {showCurtain && (
        <CurtainOpening 
          onComplete={() => {
            setShowCurtain(false);
            setCurtainPhase('content');
          }}
          color="purple"
        />
      )}

      {/* Cinematic background layers */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_mairy-chat/artifacts/uuuw6eln_477A8F7E-AD22-4B26-98F7-D73479180D44.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          opacity: 0.4
        }}
      />

      {/* Dynamic Grant spiral overlay with segment-specific effects */}
      <div className="absolute inset-0 pointer-events-none opacity-25">
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(${6 + Math.sin(spiralPhase * 0.02) * 2}) rotate(${spiralPhase * (segment.segment_number || 1)}deg)`,
            filter: 'blur(3px) brightness(1.4) hue-rotate(' + (segment.segment_number * 60) + 'deg)',
            mixBlendMode: 'screen'
          }}
        >
          <GrantitudeSpiral size={500} />
        </div>
      </div>

      {/* Cinematic gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-transparent to-black/50"></div>

      {/* Small skip button - unobtrusive */}
      <button
        onClick={skipMontage}
        className="absolute top-6 right-6 z-50 bg-black/60 hover:bg-black/80 backdrop-blur-xl rounded-lg px-3 py-2 text-white/70 hover:text-white transition-all text-sm border border-white/20"
        style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
      >
        <SkipForward className="w-4 h-4 mr-1" />
        Skip
      </button>

      {/* Cinematic Content Display */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-8">
        <div className="max-w-5xl mx-auto text-center">
          
          {/* Film-like segment presentation */}
          <div className="bg-black/70 backdrop-blur-2xl rounded-3xl p-12 border border-cyan-400/20 shadow-2xl">
            
            {/* Segment visual effects */}
            {segment.visual_effects?.includes('cosmic_spiral') && (
              <div className="flex justify-center mb-8">
                <div 
                  className="w-40 h-40 opacity-60"
                  style={{ 
                    transform: `rotate(${spiralPhase * 1.5}deg) scale(${1 + Math.sin(spiralPhase * 0.01) * 0.2})`,
                    filter: 'brightness(1.3) saturate(1.6)'
                  }}
                >
                  <GrantitudeSpiral size={160} />
                </div>
              </div>
            )}
            
            {/* Segment title with cinematic effects */}
            <h1 
              className="text-5xl md:text-6xl font-bold text-white mb-8"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 40px rgba(255, 255, 255, 0.8)',
                animation: 'fadeIn 2s ease-in-out'
              }}
            >
              {segment.title || 'The Secret History'}
            </h1>
            
            {/* Cinematic content with film-like presentation */}
            <div className="max-w-4xl mx-auto">
              <p 
                className="text-2xl md:text-3xl text-cyan-100 leading-relaxed mb-8"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 20px rgba(34, 211, 238, 0.7)',
                  animation: 'slideUp 2.5s ease-out'
                }}
              >
                {segment.content || 'Reality created through consciousness expansion...'}
              </p>
              
              {/* Uploaded content integration */}
              {segment.uploaded_content && (
                <div className="bg-purple-900/20 rounded-2xl p-6 border border-purple-400/30 mb-8">
                  <p className="text-purple-200 text-lg italic">
                    "{segment.uploaded_content.description}"
                  </p>
                  <p className="text-purple-300 text-sm mt-2">
                    — Integrated wisdom from {segment.uploaded_content.contributor}
                  </p>
                </div>
              )}
            </div>
            
            {/* Subtle progress indicator */}
            <div className="flex items-center justify-center space-x-2 mt-12 opacity-60">
              {montageContent.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-1000 ${
                    index === currentSegment ? 'bg-cyan-400 w-8' : 
                    index < currentSegment ? 'bg-green-400' : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Cinematic CSS animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      
      <PersistentFooter />
    </div>
  );
};

export default GameIntroMontage;